import { marked } from 'marked';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import Link from 'next-translate-routes/link';
import { useRouter } from 'next-translate-routes/router';
import type { FC } from 'react';
import React, { useEffect, useState } from 'react';

import {
  BankImage,
  Button,
  Container,
  FAQs,
  Icon,
  LogoClouds,
  SearchDynamic,
} from '@/components';
import { DepositCards } from '@/components/DepositCardList/parts';
import { partnerDepositFAQ } from '@/components/FAQ/faq.utils';
import { FaqSteps } from '@/components/FaqSteps';
import { FeaturesList } from '@/components/FeaturesList';
import { SafeAndSecure } from '@/components/SafeAndSecure';
import { SkeletCard } from '@/components/SkeletCard';
import { SupportedCountries } from '@/components/SupportedCountries';
import { VerificationWidget } from '@/components/VerificationWidget';
import { useApi } from '@/contexts/ApiContext';
import { useCmsContent } from '@/contexts/CmsContentContext';
import { MapDepositsFromServer } from '@/services/supabase';
import { useAuthModalsControl, useAuthStore, useSearchStore } from '@/store';
import { getTerm } from '@/store/storeSearch.utils';
import type { IBankDepositCard } from '@/types';
import { AnalyticsCategory, AnalyticsEvent } from '@/types/analytics';
import {
  analyticServices,
  capitalizeFirstLetter,
  LIST_COUNTRIES,
} from '@/utils';
import { findCountryKey } from '@/utils/countries';
import { isMobileApp } from '@/utils/isMobileApp';
import type { ProductsMeta } from '@/utils/products';

interface MultibankingPlatformLayoutProps {
  bestDeposits: IBankDepositCard[];
  initialPartnerDeposits: IBankDepositCard[];
  initialPartnerDepositsMeta: ProductsMeta;
}
export const MultibankingPlatformLayout: FC<
  MultibankingPlatformLayoutProps
> = ({ bestDeposits, initialPartnerDeposits, initialPartnerDepositsMeta }) => {
  const [isShowMoreLoading, setIsShowMoreLoading] = useState(false);
  const [deposits, setDeposits] = useState(initialPartnerDeposits);
  const [depositsMeta, setDepositsMeta] = useState(initialPartnerDepositsMeta);

  const { t } = useTranslation();
  const { setRedirectUri, openAuthModal, openSignupModal } =
    useAuthModalsControl((state) => state);
  const router = useRouter();
  const { token, residenceCountry, isTestUser } = useAuthStore(
    (state) => state,
  );
  const { seoContent, country: initialCountry } = useCmsContent();
  const { country, currency, setCurrency, terms, setIsLoading, isLoading } =
    useSearchStore((state) => state);
  const api = useApi();

  const getTermForH1 = () => {
    const selectedTerms = terms.filter((term) => term.selected);
    if (selectedTerms && selectedTerms.length === 1) {
      return getTerm(selectedTerms[0]!.key, t, false, true).toLowerCase();
    }
    return '';
  };

  const generateH1 = () => {
    return t('deposits.h1', {
      term: getTermForH1(),
      country: t(
        `database.countries.${
          capitalizeFirstLetter(
            findCountryKey(country.toLowerCase() as string),
          ) || initialCountry.name
        }`,
      ),
      currency: '',
    });
  };

  const handleGoToCompliance = () => {
    analyticServices.sendEvent({
      action: AnalyticsEvent.platformClickGetStarted,
      label: AnalyticsCategory.multibankingPlatform,
    });
    analyticServices.sendEvent({
      action: AnalyticsEvent.clickGetStartedOrOpenOnline,
      label: AnalyticsCategory.multibankingPlatform,
    });
    if (token) {
      router.push('/profile/compliance');
      return;
    }
    setRedirectUri('/profile/compliance');
    openAuthModal('platform');
  };

  const fetchDeposits = async (page = 1) => {
    if (page > 1) {
      setIsShowMoreLoading(true);
    } else {
      setIsLoading(true);
    }
    const preparedCountry = LIST_COUNTRIES.find(
      (i) => i.name.toLowerCase() === country?.toLowerCase(),
    );

    if (!preparedCountry) return;

    const depTerms = terms.filter((op) => op.selected).map((op) => op.value);

    // const depositTypes = depTypes.length > 0 ? depTypes : undefined;
    const depositTerms = depTerms.length > 0 ? depTerms : undefined;

    const { data: accounts, meta: accountsMeta } =
      await api.getDepositsListRequest('en', {
        page,
        product_types: ['fixed term deposit'],
        deposit_types: ['ordinary deposit'],
        is_partner: true,
        partner_type: 'platform',
        countries: [
          isMobileApp()
            ? residenceCountry?.value || preparedCountry.iso2
            : preparedCountry.iso2,
        ],
        terms: depositTerms,
        currencies: currency === 'all' ? undefined : [currency],
        is_test: isTestUser,
      });
    setDepositsMeta(accountsMeta);

    const preparedAccounts: IBankDepositCard[] = accounts?.map((deposit: any) =>
      MapDepositsFromServer(deposit),
    )!;
    setDeposits((prev) => {
      return page === 1 ? preparedAccounts : [...prev, ...preparedAccounts];
    });
    setIsShowMoreLoading(false);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchDeposits();
  }, [country, currency, terms]);

  useEffect(() => {
    setTimeout(() => {
      setCurrency('all');
    }, 10);
  }, []);

  return (
    <div className="relative z-10 flex h-full flex-col rounded-lg">
      {!isMobileApp() && (
        <>
          <div className="relative bg-blue-100 bg-hero-pattern lg:mx-4 lg:mb-5 lg:rounded-2xl">
            <Container className="mb-12 mt-8 max-w-[848px] px-5 lg:px-0">
              <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
                <div className="max-w-[452px]">
                  <div className="w-max rounded-full border border-blue-150 bg-white px-3 py-1">
                    <p className="flex items-center gap-1">
                      <span className="body3 font-[425]">🔥</span>
                      <span className="body3 font-[425] text-grey-800">
                        {t('multibanking-platform.label')}
                      </span>
                      <span className="body3-bold text-blue-900">
                        {initialPartnerDeposits[0]?.grossInterestRate || '5.50'}
                        %
                      </span>
                    </p>
                  </div>
                  <h1 className="h2 mt-3">{t('multibanking-platform.h1')}</h1>
                  <p className="body2-blog mt-3 text-grey-800">
                    {t('multibanking-platform.description')}
                  </p>
                  <Button
                    color="blue"
                    className="mt-6 w-full px-[37px] py-[21px] text-[15px] font-bold md:w-max"
                    props={{ onClick: handleGoToCompliance }}
                  >
                    {t('footer.Get started')}
                  </Button>
                </div>
                <div className="relative h-[402px] w-[334px] scale-[0.85] md:scale-100">
                  <Image
                    src="/assets/multibanking-platform/platform-hero.jpg"
                    alt="Platform hero"
                    height={320}
                    width={256}
                    className="absolute right-0 top-0 rounded-xl"
                  />
                  <Image
                    src="/assets/multibanking-platform/platform-mockup.png"
                    alt="Platform hero"
                    height={302}
                    width={144}
                    className="absolute bottom-0 left-0 lg:h-[302px]"
                  />
                </div>
              </div>
            </Container>
          </div>
          <Container className="flex max-w-[848px] flex-col-reverse items-center justify-between gap-6 px-5 md:flex-row md:gap-12 lg:px-0">
            <LogoClouds label={t('global.partners')} />
          </Container>
          <span className="block h-px w-full bg-blue-150" />
          <Container className="mb-16 mt-12 flex max-w-[848px] flex-col-reverse items-center justify-between gap-6 px-5 md:flex-row md:gap-12 lg:px-0">
            <FeaturesList />
          </Container>
        </>
      )}
      <div className="relative bg-blue-100 lg:mx-4 lg:mb-4 lg:rounded-2xl">
        <Container className="mb-16 mt-10 max-w-[848px] items-center justify-between gap-6 px-5 lg:px-0">
          {!isMobileApp() && (
            <h3 className="h5 md:h3 mb-6 text-blue-900">{generateH1()}</h3>
          )}
          {isMobileApp() && !token && (
            <div className="mb-6">
              <VerificationWidget
                isVerified={false}
                title={t('portfolio.unauthorized.placeholder-title')}
                description={t(
                  t('portfolio.unauthorized.placeholder-description'),
                )}
                action={{
                  title: t('header.register_now'),
                  callback: openSignupModal,
                }}
              />
            </div>
          )}
          <SearchDynamic
            className="mb-6"
            showCountry={!isMobileApp() || !token}
          />
          {isLoading ? (
            <div className="flex w-full flex-col gap-4 py-5">
              {Array(3)
                .fill(0)
                .map((_, index) => (
                  <SkeletCard key={index} className="mx-0" />
                ))}
            </div>
          ) : (
            <DepositCards
              isLoading={isShowMoreLoading}
              deposits={deposits}
              onLoadMore={() => {
                fetchDeposits(depositsMeta.page + 1);
              }}
              shouldLoadMore={
                Math.ceil(depositsMeta.total / depositsMeta.count) >
                depositsMeta.page
              }
              shouldRenderHeading={false}
              loadMoreText={t('global.show_more')}
              loadMoreClassName="min-w-[256px] py-[18px] text-[15px]"
              isBlogBankCard
              className=""
            />
          )}
        </Container>
      </div>
      {!isMobileApp() && (
        <Container className="mb-16 mt-12 flex max-w-[848px] flex-col-reverse items-center justify-between gap-6 px-5 md:flex-row md:gap-12 lg:px-0">
          <div className="md:max-w-[332px] lg:max-w-[437px]">
            <h3 className="h3 text-blue-900">
              {t('multibanking-platform.enjoy-interest.title')}
            </h3>
            <p className="body2-blog mt-3 text-grey-800">
              {t('multibanking-platform.enjoy-interest.text')}
            </p>
            <Button
              color="secondary"
              className="mt-6 w-full px-[37px] py-[21px] text-[15px] font-bold md:w-max"
              props={{ onClick: handleGoToCompliance }}
            >
              {t('footer.Get started')}
            </Button>
          </div>
          <div className="relative flex lg:h-[485px]">
            <Image
              src="/assets/multibanking-platform/platform-enjoy-interest.jpg"
              alt="Enjoy interest"
              height={485}
              width={364}
              className="rounded-[20px]"
            />
            <div className="absolute left-4 top-5 flex items-center gap-2.5 rounded-[14px] border border-blue-900 border-opacity-10 bg-[#E5E5E5E5] px-2 py-3 md:px-2.5 md:py-4">
              <span className="flex items-center justify-center rounded-lg border border-blue-900 border-opacity-10 bg-blue-500 px-[3.2px] pb-[2.88px] pt-[3.52px]">
                <Icon
                  name="PTBLogo"
                  className="size-[20px] fill-none text-white md:size-[24px]"
                />
              </span>
              <p className="body3-bold w-[88px] text-[10px] text-blue-900 md:w-[107px] md:text-[15px]">
                Interest
              </p>
              <p className="body3-bold text-[10px] text-blue-900 md:text-[15px]">
                +5,160.00 €
              </p>
            </div>
          </div>
        </Container>
      )}
      {!isMobileApp() && (
        <div className="relative bg-blue-100 lg:mx-4 lg:mb-4 lg:rounded-2xl">
          <Container className="mb-16 mt-12 flex max-w-[848px] flex-col-reverse items-center justify-between gap-6 px-5 md:flex-row lg:px-0">
            <div className="md:max-w-[340px] lg:max-w-[453px]">
              <h3 className="h3 text-blue-900">
                {t('multibanking-platform.time-to-act.title')}
              </h3>
              <p className="body2-blog mt-3 text-grey-800">
                {t('multibanking-platform.time-to-act.text')}
              </p>
              <Button
                color="blue"
                className="mt-6 w-full px-[37px] py-[21px] text-[15px] font-bold md:w-max"
                props={{ onClick: handleGoToCompliance }}
              >
                {t('footer.Get started')}
              </Button>
            </div>
            <div className="relative flex h-[350px] w-[332px] scale-[0.85] justify-end md:scale-[0.75] lg:scale-100">
              <Image
                src="/assets/multibanking-platform/platform-time-to-act.jpg"
                alt="Time to act"
                height={257}
                width={257}
                className="aspect-square h-[257px] rounded-[20px]"
              />
              {bestDeposits[0] && (
                <div className="shadow-far absolute bottom-16 left-0 flex size-max h-[148px] w-[136px] flex-col items-center rounded-[20px] bg-white p-4">
                  <BankImage
                    bankIndex={bestDeposits[0].bankIndex}
                    className="size-16"
                  />
                  <p className="body3 mt-0.5 font-[425]">
                    {bestDeposits[0].currency}
                  </p>
                  <p className="h4 mt-1 tracking-[0.2px]">
                    {bestDeposits[0].grossInterestRate}%
                  </p>
                </div>
              )}
              {bestDeposits[1] && (
                <div className="shadow-far absolute bottom-0 left-[152.5px] flex size-max h-[148px] w-[136px] flex-col items-center rounded-[20px] bg-white p-4">
                  <BankImage
                    bankIndex={bestDeposits[1].bankIndex}
                    className="size-16"
                  />
                  <p className="body3 mt-0.5 font-[425]">
                    {bestDeposits[1].currency}
                  </p>
                  <p className="h4 mt-1 tracking-[0.2px]">
                    {bestDeposits[1].grossInterestRate}%
                  </p>
                </div>
              )}
            </div>
          </Container>
        </div>
      )}
      <Container className="mb-24 mt-16 max-w-[848px] px-5 lg:px-0">
        <h3 className="mb-6 text-blue-900">
          {t('multibanking-platform.how-to-save-money.title')}
        </h3>
        <FaqSteps />
        <Link
          href="mailto:info@pickthebank.eu"
          className="mt-6 flex w-full md:w-max"
        >
          <Button
            color="secondary"
            className="w-full px-[38px] py-[21px] md:w-max"
          >
            {t('global.need-help')}
          </Button>
        </Link>
      </Container>
      <div className="relative bg-blue-900 lg:mx-4 lg:mb-4 lg:rounded-2xl">
        <Container className="my-[64px] flex max-w-[848px] flex-col items-center px-5 md:mb-[96px] lg:mt-[96px] lg:px-0">
          <p className="h3 lg:h2 text-center font-semibold tracking-[-0.64px] !text-white">
            {t('multibanking-platform.say-googbye.title')}
          </p>
          <p className="body1 mt-4 text-center font-[425] text-grey-100">
            {t('multibanking-platform.say-googbye.text')}
          </p>
          <Button
            className="mt-8 w-full px-[77px] py-[20px] md:w-max"
            color="blue"
            props={{ onClick: handleGoToCompliance }}
          >
            {t('footer.Get started')}
          </Button>
        </Container>
      </div>
      <div className="mb-4 flex flex-col gap-0 bg-blue-50 px-0 md:mx-4 md:rounded-xl">
        <Container className="mt-24 max-w-[848px] px-5 pb-[64px] lg:px-0">
          <SafeAndSecure />
        </Container>
        <span className="block h-px w-full bg-blue-150" />
        <Container className="mt-12 max-w-[848px] px-5 lg:px-0">
          <FAQs
            headingClassName="md:text-left md:text-[20px] md:font-semibold md:leading-[24px] md:tracking-[0px]"
            listClassName="md:mt-6 md:gap-3"
            blockClassName="min-h-[80px] body2 lg:min-h-[80px] md:py-[24px] md:pl-[32px] md:pr-[34px] md:tracking-[0.15px]"
            questions={partnerDepositFAQ}
          />
        </Container>
        <span className="block h-px w-full bg-blue-150" />
        <Container className="mt-16 max-w-[848px] px-5 pb-[64px] lg:px-0">
          <SupportedCountries />
        </Container>
      </div>
      {seoContent?.mainText && (
        <Container className="my-12 max-w-[850px] px-5 lg:px-0">
          <div
            dangerouslySetInnerHTML={{
              __html: marked(seoContent.mainText),
            }}
            className="markdown-article flex flex-col gap-4 md:mx-[34px] lg:mx-auto lg:flex lg:w-full lg:max-w-[849px] lg:flex-col"
          />
        </Container>
      )}
    </div>
  );
};
